import type { ProfileViewSource } from '@up2stay/up2promo-api-client';
import { createTranslator } from 'short-uuid';
import { validate as validateUUID } from 'uuid';

import { env } from '@/core/env';

const shortUuidTranslator = createTranslator();

/** Canonical short public domain for printed QRs, Wallet barcodes, and Cap deep links. */
export const PRODUCTION_PRODUCT_LINK_ORIGIN = 'https://up2.page';

/**
 * Origin used when encoding customer-facing product URLs.
 * Production / Cap release builds always use {@link PRODUCTION_PRODUCT_LINK_ORIGIN}
 * so a mis-set Vercel env cannot emit an internal forwarder host.
 * Local/dev keeps `VITE_PRODUCT_LINK_ORIGIN` for LAN / tunnel testing.
 */
export function resolveProductLinkOrigin(): string {
  if (import.meta.env.PROD) {
    return PRODUCTION_PRODUCT_LINK_ORIGIN;
  }
  const fromEnv = env.VITE_PRODUCT_LINK_ORIGIN?.replace(/\/+$/, '');
  return fromEnv || PRODUCTION_PRODUCT_LINK_ORIGIN;
}

/**
 * Builds a printed product URL from a hash
 * @param hash - Product hash
 * @returns Product URL
 */
export function buildProductLinkUrl(hash: string): string {
  return `${resolveProductLinkOrigin()}/${hash}`;
}

/**
 * Builds a profile's stable public digital QR / Wallet / vanity URL from its
 * `publicHash` (`{productLinkOrigin}/p/{hash}?src=...`). Mirrors the
 * backend's `/p/:hash` redirect — never the direct profile viewer URL.
 * Prefer {@link ProfileViewSource} `wallet` / `profile_qr` for encode sites.
 */
export function buildPublicProfileHashUrl(
  publicHash: string,
  source: Extract<ProfileViewSource, 'wallet' | 'profile_qr'>,
): string {
  return `${resolveProductLinkOrigin()}/p/${publicHash}?src=${source}`;
}

const PRODUCT_TYPE_CODES = ['GO', 'IG', 'ME', 'FB', 'TT', 'CU', 'BC'] as const;

/** Matches backend product hash validation (short-uuid or RFC UUID). */
export function isProductHash(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return shortUuidTranslator.validate(trimmed, true) || validateUUID(trimmed);
}

/**
 * Parses a scanned QR/barcode value (URL or raw hash) into hash and optional productType.
 * If the value is a URL, extracts the first path segment as hash and ?t= as productType.
 * Works with any domain (e.g. the forwarder, staging, or NFC-written URLs).
 */
export function parseScannedProductValue(value: string): {
  hash: string;
  productType?: string;
} {
  const trimmed = value.trim();
  if (!trimmed) {
    return { hash: '' };
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    try {
      url = new URL(`https://${trimmed}`);
    } catch {
      return { hash: trimmed };
    }
  }
  const pathSegment = url.pathname.replace(/^\/+|\/+$/g, '').split('/')[0];
  const hash = pathSegment || trimmed;
  const t = url.searchParams.get('t');
  const productType =
    t && PRODUCT_TYPE_CODES.includes(t as (typeof PRODUCT_TYPE_CODES)[number])
      ? t
      : undefined;
  return { hash: hash || trimmed, productType };
}

const NDEF_URI_PREFIXES = [
  '',
  'http://www.',
  'https://www.',
  'http://',
  'https://',
  'tel:',
  'mailto:',
  'ftp://anonymous:anonymous@',
  'ftp://ftp.',
  'ftps://',
  'sftp://',
  'smb://',
  'nfs://',
  'ftp://',
  'dav://',
  'news:',
  'telnet://',
  'imap:',
  'rtsp://',
  'urn:',
  'pop:',
  'sip:',
  'sips:',
  'tftp:',
  'btspp://',
  'btl2cap://',
  'btgoep://',
  'tcpobex://',
  'irdaobex://',
  'file://',
  'urn:epc:id:',
  'urn:epc:tag:',
  'urn:epc:pat:',
  'urn:epc:raw:',
  'urn:epc:',
  'urn:nfc:',
];

/**
 * Decode NDEF record payload to string (UTF-8). Used for NFC tag content.
 */
function ndefPayloadToString(payload: number[]): string {
  if (!payload?.length) return '';
  try {
    return new TextDecoder('utf-8').decode(new Uint8Array(payload));
  } catch {
    return '';
  }
}

/**
 * Extract first URL or text content from NDEF records for product hash/URL.
 * Supports well-known URI (type [0x55]) and text (type [0x54]) records.
 */
export function ndefMessageToValue(
  ndefMessage:
    | Array<{ type?: number[]; payload?: number[] }>
    | null
    | undefined,
): string {
  if (!ndefMessage?.length) return '';
  for (const record of ndefMessage) {
    const type = record.type;
    const payload = record.payload ?? [];
    if (!payload.length) continue;
    // Well-known type: URI = 0x55
    if (type?.length === 1 && type[0] === 0x55) {
      const prefixIndex = payload[0] ?? 0;
      const prefix = NDEF_URI_PREFIXES[prefixIndex] ?? '';
      const rest = ndefPayloadToString(payload.slice(1));
      return prefix + rest;
    }
    // Well-known type: Text = 0x54
    if (type?.length === 1 && type[0] === 0x54) {
      // First byte: status (bit 0-4 = language code length)
      const langLen = (payload[0] ?? 0) & 0x1f;
      return ndefPayloadToString(payload.slice(1 + langLen));
    }
    // Fallback: decode whole payload as text
    const decoded = ndefPayloadToString(payload);
    if (decoded.trim()) return decoded;
  }
  return '';
}

/**
 * Manual entry of a product code: accepts the bare code or the full sticker
 * link (same parsing as a QR scan). Null when it is not a product code.
 */
export function parseManualProductCode(
  value: string,
): { hash: string; productType?: string } | null {
  const parsed = parseScannedProductValue(value);
  return parsed.hash && isProductHash(parsed.hash) ? parsed : null;
}
